import React, { useEffect, useState } from "react";
import { AuthContextType, useAuth } from "../../../contexts/AuthContext";
import { useError } from "../../../contexts/ErrorContext";
import useApi from "../../../hooks/useGaneshApi";

import { patchBrandMetadata } from "../../../services/api";
import { BrandMetadata_T } from "../../../types/Brand";

import { Box, Button, TextField } from "@mui/material";
import { RiExternalLinkLine } from "@remixicon/react";
import BrandReviewInstructions from "../UI/BrandReviewInstructions";
import "../BrandMetadataReview.css";

import {
  decodeHtmlEntities,
  encodeHtmlEntities,
} from "../../../utils/common";

interface BrandMetadataReviewProps {
  brand_id: string;
}
const ReviewGeneralBrandMetadata: React.FC<BrandMetadataReviewProps> = ({
  brand_id,
}) => {
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [originalNotes, setOriginalNotes] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [wikipedia, setWikipedia] = useState("");
  const [originalWikipedia, setOriginalWikipedia] = useState("");
  const [isWikipediaEditing, setIsWikipediaEditing] = useState(false);

  const { showError } = useError();
  const { apiRequest } = useApi();
  const userAuth: AuthContextType = useAuth();

  useEffect(() => {
    if (brand_id) {
      const fetchBrandGeneralMetadata = async () => {
        try {
          const response = await apiRequest<BrandMetadata_T>(
            `/brand_metadata/general_metadata/${brand_id}`,
            {
              method: "GET",
            }
          );
          if (response.data) {
            const decodedNotes = decodeHtmlEntities(response.data.notes || "");
            setNotes(decodedNotes);
            setOriginalNotes(decodedNotes);
            setWikipedia(response.data.wikipedia || "");
            setOriginalWikipedia(response.data.wikipedia || "");
          }
        } catch (error) {
          console.error(error);
          showError(`Failed to fetch brand notes and wiki for ${brand_id}.`);
        } finally {
          setLoading(false);
        }
      };
      fetchBrandGeneralMetadata();
    }
  }, [brand_id]);

  /* COMMENTING OUT EDITING PIECES FOR NOW */

  // const handleEditToggle = (field: "notes" | "wikipedia") => {
  //   if (field === "notes") {
  //     setIsEditing((prev) => !prev);
  //   } else if (field === "wikipedia") {
  //     setIsWikipediaEditing((prev) => !prev);
  //   }
  // };

  // const handleFieldChange = (field: "notes" | "wikipedia", value: string) => {
  //   if (field === "notes") {
  //     setNotes(value);
  //   } else if (field === "wikipedia") {
  //     setWikipedia(value);
  //   }
  // };

  // const handleUpdate = async (field: "notes" | "wikipedia") => {
  //   const updatedValue = field === "notes" ? notes : wikipedia;

  //   const metadata = {
  //     data: {
  //       attributes: {
  //         [field]: encodeHtmlEntities(updatedValue),
  //       },
  //     },
  //   };

  //   await patchBrandMetadata(userAuth.authAttributes, brand_id, metadata);

  //   if (field === "notes") {
  //     setOriginalNotes(notes);
  //   } else if (field === "wikipedia") {
  //     setOriginalWikipedia(wikipedia);
  //   }
  //   handleEditToggle(field);
  // };

  // const handleCancelEdit = (field: "notes" | "wikipedia") => {
  //   if (field === "notes") {
  //     setNotes(originalNotes);
  //     handleEditToggle("notes");
  //   } else if (field === "wikipedia") {
  //     setWikipedia(originalWikipedia);
  //     handleEditToggle("wikipedia");
  //   }
  // };

  const handleOpenWikipedia = () => {
    if (wikipedia) {
      window.open(wikipedia, "_blank");
    }
  };

  const brand_notes_instructions = {
    section_id: "brand-notes",
    instruction_title: "Review Brand Notes",
    purpose:
      "Notes about the brand are for internal use to provide a description and context about the brand. These notes help internal teams understand the brand's identity, ownership, and operations more effectively.",
    goal: "Ensure notes about the brand are accurate, up-to-date, and reflected in the metadata.",
    task_description:
      "Review the content in the `notes` attribute from the metadata for the brand to ensure it is complete and accurate.",
    task_steps: [
      {
        title: "Review the Current Content in the `Notes` Attribute",
        instructions: [
          "Check the existing notes to ensure they provide clear and relevant information about the brand.",
          {
            text: "Verify that the notes include key details such as:",
            sublist: [
              "Brand ownership or parent company, if applicable.",
              "Countries or regions where the brand operates.",
              "Unique aspects of the brand's operations or offerings.",
            ],
          },
        ],
      },
      {
        title: "Identify Missing or Outdated Information",
        instructions: [
          "Compare the notes with other reliable sources such as the brand's official website, corporate announcements, or trusted business directories.",
          "Highlight any information that is missing, outdated, or incorrect.",
        ],
      },
      {
        title: "Update the Metadata",
        instructions: [
          "Navigate to the brand in Kuebiko.",
          "Select Brand Edit.",
          "Update the `notes` attribute with the corrected or additional information.",
        ],
      },
    ],
  };

  const wikipedia_instructions = {
    section_id: "brand-wikipedia",
    instruction_title: "Review Wikipedia URL",
    purpose:
      "The wikipedia attribute provides a link to the brand's Wikipedia page. Importantly, the wikipedia page is leveraged by the AI algorithms that source and populate several of the metadata attributes. It also helps internal teams and external users quickly access additional information about the brand.",
    goal: "Ensure the `wikipedia` attribute contains an accurate and up-to-date link to the brand's Wikipedia page.",
    task_description:
      "Review the content in the `wikipedia` attribute from the metadata for the brand to ensure the link is valid, accurate, and relevant.",
    task_steps: [
      {
        title: "Review the Current Content in the Wikipedia Attribute",
        instructions: [
          "Check the existing Wikipedia link in the `wikipedia` attribute.",
          "Verify that the link points to the correct Wikipedia page for the brand.",
        ],
      },
      {
        title: "Test the Link",
        instructions: [
          "Open the link in a browser to confirm it:",
          {
            text: "Ensure the link:",
            sublist: [
              "Loads successfully without errors.",
              "Points to an active and accurate Wikipedia page for the brand.",
            ],
          },
          "Check the content on the Wikipedia page to ensure it is relevant and provides up-to-date information about the brand.",
        ],
      },
      {
        title: "Identify Missing or Incorrect Links",
        instructions: [
          "If the `wikipedia` attribute is empty or the link is invalid:",
          {
            text: "Take the following actions:",
            sublist: [
              "Search Wikipedia to locate the correct page for the brand.",
            ],
          },
        ],
      },
      {
        title: "Update the Metadata",
        instructions: [
          "Navigate to the brand in Kuebiko.",
          "Select Brand Edit.",
          "Update the `wikipedia` attribute with the correct and validated link.",
        ],
      },
    ],
  };

  return (
    <Box>
      <div className="metadata-review-container">
        <BrandReviewInstructions {...brand_notes_instructions} />
        {/* Review and Update Notes */}
        <Box style={{ marginTop: "20px" }}>
          <TextField
            label="Brand Notes"
            multiline
            rows={6}
            value={notes}
            //  onChange={(e) => handleFieldChange("notes", e.target.value)}
            disabled={!isEditing}
            fullWidth
            variant="outlined"
            InputProps={{
              style: {
                color: isEditing ? "inherit" : "#333",
              },
            }}
          />
          {/* <Box style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
            <Button
              variant="contained"
              color="primary"
              onClick={
                isEditing
                  ? () => handleUpdate("notes")
                  : () => handleEditToggle("notes")
              }
            >
              {isEditing ? "Update" : "Edit"}
            </Button>
            {isEditing && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => handleCancelEdit("notes")}
              >
                Cancel Edit
              </Button>
            )}
          </Box> */}
        </Box>
      </div>
      <div className="metadata-review-container">
        {/* Section: Wikipedia */}
        <BrandReviewInstructions {...wikipedia_instructions} />

        {/* Review and Update Wikipedia */}
        <Box style={{ marginTop: "20px" }}>
          <TextField
            label="Wikipedia Link"
            value={wikipedia}
            //      onChange={(e) => handleFieldChange("wikipedia", e.target.value)}
            disabled={!isWikipediaEditing}
            fullWidth
            variant="outlined"
            InputProps={{
              style: {
                color: isWikipediaEditing ? "inherit" : "#333",
              },
            }}
          />
          <Box style={{ marginTop: "10px", display: "flex", gap: "10px" }}>
            {/* <Button
              variant="contained"
              color="primary"
              onClick={
                isWikipediaEditing
                  ? () => handleUpdate("wikipedia")
                  : () => handleEditToggle("wikipedia")
              }
            >
              {isWikipediaEditing ? "Update" : "Edit"}
            </Button> */}
            {/* {isWikipediaEditing && (
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => handleCancelEdit("wikipedia")}
              >
                Cancel Edit
              </Button>
            )} */}
            {wikipedia && !isWikipediaEditing && (
              <Button
                variant="text"
                color="primary"
                onClick={handleOpenWikipedia}
                startIcon={<RiExternalLinkLine />}
              >
                Open Wikipedia Page
              </Button>
            )}
          </Box>
        </Box>
      </div>
    </Box>
  );
};

export default ReviewGeneralBrandMetadata;
